import { type Locale } from '@aysar/contracts';

import { formatNumber } from '../format-number/format-number.util';

/**
 * An amount of money, with the code beside it.
 *
 * ## The unit is a CODE, never a glyph
 *
 * `currencyDisplay: 'code'` — the generalisation of P10.4's finding that a
 * symbol garbles under bidi, and the same choice `MoneyInput` makes for the box
 * itself. Note that `Intl` separates the code from the amount with a
 * **non-breaking space** (U+00A0), so a spec comparing against a normal space
 * fails against a diff showing two identical strings (P22b4).
 *
 * ## No currency is a real state, and it is not an excuse to guess one
 *
 * `currency` is `null` while `/app-settings` is still loading, or where a
 * deployment has not been asked. The amount is then rendered as a plain
 * two-decimal number: a figure without its unit is honest, a figure in a unit
 * this deployment does not use is a price nobody quoted (U16). The same
 * degradation `discountWorth` makes, and it is shared with it so the portal
 * cannot render one amount two ways.
 */
export function formatMoney(amount: number, locale: Locale, currency: string | null): string {
  return formatNumber(
    amount,
    locale,
    currency === null || currency === ''
      ? { minimumFractionDigits: 2, maximumFractionDigits: 2 }
      : { style: 'currency', currency, currencyDisplay: 'code' },
  );
}
